import { Component, inject, input, linkedSignal, signal } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatError, MatFormField, MatHint, MatLabel, MatPrefix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import {
  AddressChanged,
  DepAddressTypeaheadComponent,
  InitialAddress,
} from 'src/app/shared/components/dep-address-typeahead/dep-address-typeahead.component';
import { MatSlideToggle, MatSlideToggleChange } from '@angular/material/slide-toggle';
import { ChargingStationLocationCreatedEditDialogService } from '../../charging-station-location-created-edit-dialog.service';
import { InputMaskModule } from '@ngneat/input-mask';
import { MatDialog } from '@angular/material/dialog';
import { ChargingStationLocationMapAddressPickerModalComponent } from '../../charging-station-location-map-address-picker-modal/charging-station-location-map-address-picker-modal.component';
import { MatOption, MatSelect, MatSuffix } from '@angular/material/select';
import { EnumToArrayPipe } from '../../../../../shared/pipes/enum-to-array.pipe';
import {
  TranslateLocationAppVisibilityPipe,
  TranslateLocationStatusEnum,
} from '../../../../../shared/pipes/translate-location-status-enum.pipe';
import { MatIcon } from '@angular/material/icon';
import { AppVisibilityStatus, LocationAppVisibility } from '../../../charging-station-location-model';
import { FeedbackInfoComponent, FormFieldLoadingComponent } from 'ok-angular-shared/components';
import { MatButton, MatIconButton } from '@angular/material/button';
import { CustomDropDownArrowDirective } from 'ok-angular-shared/directives';
import { ADDRESS_MAX_LENGTH } from './danish-address-rules';

export interface AddressForm {
  street: FormControl<string>;
  houseNumber: FormControl<string>;
  houseLetter: FormControl<string>;
  zipCode: FormControl<string>;
  town: FormControl<string>;
  floor: FormControl<string | null>;
  door: FormControl<string | null>;
  darId: FormControl<string | null>;
}

export interface Step2Form {
  name: FormControl<string | null>;
  addressWithoutDARValidation: FormControl<boolean>;
  address: FormGroup<AddressForm>;
  locationAddress: FormControl<string | null>;
  latitude: FormControl<string>;
  longitude: FormControl<string>;
  // Metering point identifiers for the charging station location
  meteringPointIdentifiers: FormArray<FormControl<string>>;
  hasOkPowerMeter: FormControl<boolean | null>;
  restricted: FormControl<boolean>;
  //app ikon
  locationAppVisibilityStatus: FormControl<AppVisibilityStatus>;
  locationAppVisibility: FormControl<LocationAppVisibility>;
  appLocationText: FormControl<string | null>;
  conceptInstallationNumber: FormControl<string | null>;
}

@Component({
  selector: 'adm-csl-step2',
  templateUrl: './step2.component.html',
  styleUrl: './step2.component.scss',
  imports: [
    MatFormField,
    MatInput,
    MatOption,
    MatButton,
    MatSelect,
    InputMaskModule,
    MatLabel,
    MatError,
    DepAddressTypeaheadComponent,
    MatSlideToggle,
    MatHint,
    EnumToArrayPipe,
    TranslateLocationAppVisibilityPipe,
    TranslateLocationStatusEnum,
    MatIcon,
    MatIconButton,
    FeedbackInfoComponent,
    FormFieldLoadingComponent,
    NgTemplateOutlet,
    ReactiveFormsModule,
    MatSuffix,
    MatPrefix,
    CustomDropDownArrowDirective,
  ],
})
export class ChargingStationLocationEditDialogStep2Component {
  service = inject(ChargingStationLocationCreatedEditDialogService);
  form = input.required<FormGroup<Step2Form>>();
  initialAddress = input<InitialAddress | null>(null);
  noValidNetArea = input<boolean>(false);
  checkingNetArea = input<boolean>(false);
  typeaheadInitialAddress = linkedSignal(() => this.initialAddress());
  dialog = inject(MatDialog);
  appVisibilityStatuses: typeof AppVisibilityStatus = AppVisibilityStatus;
  locationAppVisibilities: typeof LocationAppVisibility = LocationAppVisibility;
  locationAppVisibilityOptions = Object.values(LocationAppVisibility);
  readonly addressMaxLength = ADDRESS_MAX_LENGTH;

  public readonly meteringPointIdentifierDraft = new FormControl('', {
    nonNullable: true,
    validators: [Validators.pattern(/^\d{11}$/)],
  });
  public readonly editingMeteringPointIdentifierIndex = signal<number | null>(null);

  get meteringPointIdentifiers(): FormArray<FormControl<string>> {
    return this.form().controls.meteringPointIdentifiers;
  }

  public editMeteringPointIdentifier(index: number): void {
    this.meteringPointIdentifierDraft.setValue(this.meteringPointIdentifiers.at(index).value);
    this.editingMeteringPointIdentifierIndex.set(index);
  }

  public saveMeteringPointIdentifier(): void {
    const draft = this.meteringPointIdentifierDraft;
    const editingIndex = this.editingMeteringPointIdentifierIndex();

    if (!draft.value || draft.invalid) {
      draft.markAsTouched();
      return;
    }

    if (this.hasDuplicateMeteringPointIdentifier()) {
      draft.setErrors({ ...draft.errors, duplicate: true });
      draft.markAsTouched();
      return;
    }

    if (editingIndex === null) {
      this.meteringPointIdentifiers.push(
        new FormControl(draft.value, {
          nonNullable: true,
          validators: [Validators.pattern(/^\d{11}$/)],
        })
      );
    } else {
      this.meteringPointIdentifiers.at(editingIndex).setValue(draft.value);
    }

    this.meteringPointIdentifiers.markAsDirty();
    this.cancelMeteringPointIdentifierEdit();
  }

  public cancelMeteringPointIdentifierEdit(): void {
    this.editingMeteringPointIdentifierIndex.set(null);
    this.meteringPointIdentifierDraft.reset('');
  }

  public removeMeteringPointIdentifier(index: number): void {
    this.meteringPointIdentifiers.removeAt(index);
    this.meteringPointIdentifiers.markAsDirty();
    this.cancelMeteringPointIdentifierEdit();
  }

  public hasDuplicateMeteringPointIdentifier(): boolean {
    const editingIndex = this.editingMeteringPointIdentifierIndex();

    return this.meteringPointIdentifiers.controls.some(
      (control, index) => index !== editingIndex && control.value === this.meteringPointIdentifierDraft.value
    );
  }

  public onAddressValidationToggle(event: MatSlideToggleChange): void {
    if (event.checked) {
      return;
    }
    this.form().controls.address.reset();
    this.form().controls.locationAddress.setValue(null);
    this.service.addressFoundAndSelected.set(false);
    this.typeaheadInitialAddress.set(null);
  }

  public selectAddress(address: AddressChanged | null) {
    if (address === null || address.address === null || address.address === undefined) {
      return;
    }
    this.service.addressFoundAndSelected.set(true);

    this.form().patchValue({
      locationAddress: address.formatAddressToString,
      address: {
        street: address.address.road,
        houseNumber: address.address.number.toString(),
        houseLetter: address.address.letter ?? '',
        town: address.address.city,
        zipCode: address.address.postalCode,
        floor: address.address.floor || null,
        door: address.address.door || null,
        darId: address.darId?.toString() ?? null,
      },
      latitude: '',
      longitude: '',
    });
  }

  charactersOnly(event: KeyboardEvent): boolean {
    const pattern = /^[a-zA-Z]$/;
    const result = pattern.test(event.key);
    return result;
  }

  openMap() {
    const dialogRef = this.dialog.open(ChargingStationLocationMapAddressPickerModalComponent, {
      width: '1224px',
      disableClose: false,
      data: {
        preselectedLocation: this.service.addressFoundAndSelected() ? (this.form().controls.locationAddress.value ?? undefined) : undefined,
        preselectedLatitude: this.form().controls.latitude.value
          ? parseFloat(this.form().controls.latitude.value.replace(',', '.'))
          : undefined,
        preselectedLongitude: this.form().controls.longitude.value
          ? parseFloat(this.form().controls.longitude.value.replace(',', '.'))
          : undefined,
      },
    });

    dialogRef.afterClosed().subscribe(data => {
      if (data) {
        const lat = data.lat.replace('.', ',');
        const lng = data.lng.replace('.', ',');
        this.form().patchValue({
          latitude: lat,
          longitude: lng,
        });
      }
    });
  }
}
